/**
 * Copyright (c) Mayoral Moda Infantil, S.A.U. and affiliates.
 */

import {
  ApolloCache,
  ApolloClient,
  ApolloLink,
  fromPromise,
  HttpLink,
  NormalizedCacheObject,
  Observable,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { RetryLink } from '@apollo/client/link/retry';
import { RefreshTokensDocument, RefreshTokensMutation } from '@sdk/core';
import { readFromCookies, writeToCookies } from '@utils/cookies';
import logger from '@utils/logger';
import { logout } from '@utils/session';
import { sha256 } from 'crypto-hash';
import merge from 'deepmerge';
import { useMemo } from 'react';

import { getCacheConfiguration, langIdVar, loggedInVar, seasonVar, workshopVar } from './cache';

import config from '@/config';
import { AuthCookieExpiration, StorageTokens } from '@/lib/constants';

let pendingSessionRefresh: Promise<boolean> = null;

// only load persisted query link if it's available (i.e. in localhost or in TLS context and only on client-side)
const getPersistedQueryLinks = (): ApolloLink[] => {
  if (
    typeof window !== 'undefined' &&
    window.crypto.subtle &&
    process.env.NODE_ENV === 'production'
  ) {
    return [
      createPersistedQueryLink({
        sha256,
        // TODO: Find out why apollo it's removing our headers from the request
        //  when `useGETForHashedQueries` is set to true.
        //  We use those headers to generate a unique cache key for the user,
        //  so we need to make sure they're not removed.
        useGETForHashedQueries: false,
      }),
    ];
  } else {
    return [];
  }
};

// refresh session tokens
const refreshSessionTokens = async (
  apolloClient: ApolloClient<NormalizedCacheObject>,
): Promise<RefreshTokensMutation> => {
  const { data } = await apolloClient.mutate<RefreshTokensMutation>({
    mutation: RefreshTokensDocument,
  });

  const {
    refreshTokens: { token, refreshToken },
  } = data;

  writeToCookies(StorageTokens.TOKEN, token, {
    expires: AuthCookieExpiration,
  });
  writeToCookies(StorageTokens.REFRESH_TOKEN, refreshToken, {
    expires: AuthCookieExpiration,
  });

  loggedInVar(true);

  return data;
};

function createApolloClient(): ApolloClient<NormalizedCacheObject> {
  const client = new ApolloClient({
    name: 'client-side',
    link: ApolloLink.from([
      ...getPersistedQueryLinks(),
      new RetryLink({
        delay: {
          initial: 300,
          max: Infinity,
          jitter: true,
        },
        attempts: {
          max: 2,
        },
      }),
      // refresh or reset login status on 403 / 401 response
      onError(({ graphQLErrors, operation, forward }) => {
        graphQLErrors = graphQLErrors || [];

        const sessionExpired = graphQLErrors.some(
          (error) => error.extensions.code === 'UNAUTHENTICATED',
        );

        // The list of operations we want to ignore when refreshing the session.
        const blacklistedOperations = ['RefreshTokens', 'Login'];

        // If the session has expired and the operation it's not on the blacklist,
        // handle the refresh token request.
        if (sessionExpired && !blacklistedOperations.includes(operation.operationName)) {
          if (!pendingSessionRefresh) {
            pendingSessionRefresh = refreshSessionTokens(client)
              .then(() => {
                return true;
              })
              .catch(() => {
                // Handle token refresh errors (e.g. clear stored tokens, redirect to login, ...)
                const cache: ApolloCache<object> = operation.getContext().cache;
                logout(cache);

                return false;
              })
              .finally(() => {
                pendingSessionRefresh = null;
              });
          }

          return fromPromise(pendingSessionRefresh).flatMap((success) => {
            // If the session was not refreshed, we need to cancel all pending requests.
            // Return an observable that gracefully cancels all pending requests.
            if (!success) {
              logger.debug('Session refresh failed, cancelling pending request', {
                operationName: operation.operationName,
              });
              return new Observable((observer) => {
                // With this we prevent retrying in case of multiple 401 errors,
                // we cant use observer.error because it would trigger retries.
                observer.next({
                  data: null,
                  errors: [],
                });
                observer.complete();
              });
            }

            // If the session was refreshed, we need to retry the request.
            return forward(operation);
          });
        }
      }),
      // send authentication related headers
      setContext((request, { headers }) => {
        const langId = langIdVar();
        const season = seasonVar();
        const supplier = workshopVar();

        // get the authentication cookies
        let token = readFromCookies<string>(StorageTokens.TOKEN);
        const refreshToken = readFromCookies<string>(StorageTokens.REFRESH_TOKEN);

        if (request.operationName === 'RefreshTokens') {
          token = refreshToken;
        }

        // return the headers to the context so httpLink can read them
        return {
          headers: {
            ...headers,
            ...((token && { token }) || {}),
            ...((refreshToken && { refreshToken }) || {}),
            ...((langId && { langId }) || {}),
            ...((season && { season: season?.value }) || {}),
            ...((supplier && { supplier }) || {}),
          },
        };
      }),
      new HttpLink({ uri: config.public.graphqlUrl }),
    ]),
    cache: getCacheConfiguration(),
    connectToDevTools: process.env.NODE_ENV !== 'production',
  });
  return client;
}

// NOTE: Under no circumstances should this be assigned inside SSR!
// It will end up causing memory problems. This is needed for client-side for Apollo development tools to work properly.
let apolloClient: ApolloClient<NormalizedCacheObject>;

export function initializeApollo(initialState = null): ApolloClient<NormalizedCacheObject> {
  const _apolloClient = apolloClient ?? createApolloClient();

  // If your page has Next.js data fetching methods that use Apollo Client,
  // the initial state gets hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = _apolloClient.extract();

    // Merge the existing cache into data passed from getStaticProps/getServerSideProps
    const data = merge(initialState, existingCache);

    // Restore the cache with the merged data
    _apolloClient.cache.restore(data);
  }

  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') {
    return _apolloClient;
  }

  // Create the Apollo Client once in the client
  if (!apolloClient) {
    apolloClient = _apolloClient;
  }

  return _apolloClient;
}

export function useApollo(initialState: unknown): ApolloClient<NormalizedCacheObject> {
  return useMemo(() => initializeApollo(initialState), [initialState]);
}
